import { useTranslation, translate } from '@rhino-project/core/i18n';
import { useEffect, useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Button,
  Card,
  FieldGroup,
  Fieldset,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Select,
  SelectIndicator,
  SelectPopover,
  SelectTrigger,
  SelectValue,
  TextField,
  toast,
  Typography
} from '@heroui/react';
import { useModelShow, useModelUpdate } from '@rhino-project/core/hooks';
import {
  computeStanding,
  formatPercent,
  recordEntries,
  resolvePointSystem,
  type PointSystem
} from '../../../../../utils/standings';
import { RecordCard } from '../../../../../components/shared/RecordCard';
import type { RhinoRead } from '@rhino-project/core';

type StatField =
  | 'wins'
  | 'losses'
  | 'ties'
  | 'overtime_wins'
  | 'shootout_wins'
  | 'overtime_losses'
  | 'shootout_losses'
  | 'points'
  | 'win_pct'
  | 'goals_for'
  | 'goals_against'
  | 'power_play_pct'
  | 'penalty_kill_pct';

type StatFields = Record<StatField, string>;

const STAT_FIELDS: StatField[] = [
  'wins',
  'losses',
  'ties',
  'overtime_wins',
  'shootout_wins',
  'overtime_losses',
  'shootout_losses',
  'points',
  'win_pct',
  'goals_for',
  'goals_against',
  'power_play_pct',
  'penalty_kill_pct'
];

const pointSystemOptions = (): { id: PointSystem; label: string }[] => [
  { id: '2pt', label: translate('frontend.messages.2_point_nhl') },
  { id: '3pt', label: translate('frontend.messages.3_point_iihf') },
  { id: 'custom', label: translate('frontend.messages.custom') }
];

const pickStats = (record: RhinoRead<'team_season'> | null): StatFields => {
  const source = (record?.stats ?? {}) as Record<string, unknown>;
  return STAT_FIELDS.reduce((acc, f) => {
    acc[f] = String(source[f] ?? 0);
    return acc;
  }, {} as StatFields);
};

const toNumber = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

type StatFieldProps = {
  label: string;
  field: StatField;
  stats: StatFields;
  onChange: (field: StatField, value: string) => void;
};

const StatFieldInput = ({ label, field, stats, onChange }: StatFieldProps) => (
  <TextField
    variant="secondary"
    value={stats[field]}
    onChange={(v) => onChange(field, v)}
  >
    <Label>{label}</Label>
    <Input inputMode="decimal" />
  </TextField>
);

export const AdminStats = () => {
  const { t } = useTranslation();

  const { team_season_id } = useParams({ strict: false }) as {
    team_season_id: string;
  };
  const teamSeasonId = Number(team_season_id);
  const { resource } = useModelShow('team_season', teamSeasonId);
  const teamSeason = resource;

  const { mutate: updateTeamSeason, isPending } = useModelUpdate('team_season');

  // eslint-disable-next-line @eslint-react/use-state -- Preserve existing behavior while enabling the rule globally.
  const [stats, setStats] = useState<StatFields>(pickStats(null));
  const [pointSystem, setPointSystem] = useState<PointSystem>('custom');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!teamSeason) return;
    const source = (teamSeason.stats ?? {}) as Record<string, unknown>;
    const loaded = pickStats(teamSeason);
    // Custom mode lets the coach edit the win % directly. Seed the field with the
    // raw win rate when none was saved, so legacy seasons show a real number.
    if (source.win_pct === undefined || source.win_pct === null) {
      const raw = computeStanding({ ...source, point_system: 'custom' });
      loaded.win_pct = (raw.percentage * 100).toFixed(1);
    }
    // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- Preserve existing behavior while enabling the rule globally.
    setStats(loaded);
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- Preserve existing behavior while enabling the rule globally.
    setPointSystem(resolvePointSystem(source.point_system));
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- Preserve existing behavior while enabling the rule globally.
    setDirty(false);
  }, [teamSeason]);

  const handleChange = (field: StatField, value: string) => {
    setStats((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSystemChange = (value: PointSystem) => {
    setPointSystem(value);
    setDirty(true);
  };

  const handleSave = () => {
    const statsPayload: Record<string, number | string> = {};
    STAT_FIELDS.forEach((f) => {
      statsPayload[f] = toNumber(stats[f]);
    });
    statsPayload.point_system = pointSystem;
    updateTeamSeason({ id: teamSeasonId, stats: statsPayload } as never, {
      onSuccess: () => {
        setDirty(false);
        toast.success(t('frontend.messages.season_statistics_saved'));
      }
    });
  };

  const liveStats = {
    point_system: pointSystem,
    wins: toNumber(stats.wins),
    losses: toNumber(stats.losses),
    ties: toNumber(stats.ties),
    overtime_wins: toNumber(stats.overtime_wins),
    shootout_wins: toNumber(stats.shootout_wins),
    overtime_losses: toNumber(stats.overtime_losses),
    shootout_losses: toNumber(stats.shootout_losses),
    points: toNumber(stats.points),
    win_pct: toNumber(stats.win_pct)
  };
  const standing = computeStanding(liveStats);
  const record = recordEntries(liveStats);

  // When wins are broken out into regulation/OT/SO (3-point and custom), the base
  // "Wins" input captures regulation wins only, with OT/SO wins entered separately.
  const showWinBreakdown = pointSystem === '3pt' || pointSystem === 'custom';
  const winsLabel = showWinBreakdown
    ? t('frontend.messages.regulation_wins')
    : t('frontend.messages.wins');

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Typography.Heading level={1}>
          {t('frontend.messages.season_statistics')}
        </Typography.Heading>
        <Button
          variant="primary"
          onPress={handleSave}
          isDisabled={isPending || !dirty}
        >
          {t('frontend.messages.save')}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <RecordCard entries={record} />
        <Card className="text-center">
          <p className="text-3xl font-bold">{standing.points}</p>
          <p className="text-xs text-muted uppercase mt-1">
            {t('frontend.messages.points')}
          </p>
        </Card>
        <Card className="text-center">
          <p className="text-3xl font-bold text-accent">
            {formatPercent(standing.percentage)}
          </p>
          <p className="text-xs text-muted uppercase mt-1">
            {standing.percentageLabel}
          </p>
        </Card>
        <Card className="text-center">
          <p className="text-3xl font-bold">{standing.gamesPlayed}</p>
          <p className="text-xs text-muted uppercase mt-1">
            {t('frontend.messages.games_played')}
          </p>
        </Card>
      </div>

      <div className="flex flex-col gap-6">
        <Card className="p-6">
          <Card.Header>
            <Card.Title>{t('frontend.messages.record')}</Card.Title>
          </Card.Header>
          <Card.Content>
            <Fieldset>
              <FieldGroup>
                <div className="max-w-xs">
                  <Select
                    value={pointSystem}
                    onChange={(key) => handleSystemChange(key as PointSystem)}
                    aria-label={t('frontend.messages.point_system')}
                    variant="secondary"
                  >
                    <SelectTrigger>
                      <SelectValue />
                      <SelectIndicator />
                    </SelectTrigger>
                    <SelectPopover>
                      <ListBox items={pointSystemOptions()}>
                        {(opt: { id: PointSystem; label: string }) => (
                          <ListBoxItem
                            id={opt.id}
                            key={opt.id}
                            textValue={opt.label}
                          >
                            {opt.label}
                          </ListBoxItem>
                        )}
                      </ListBox>
                    </SelectPopover>
                  </Select>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatFieldInput
                    label={winsLabel}
                    field="wins"
                    stats={stats}
                    onChange={handleChange}
                  />
                  {showWinBreakdown && (
                    <>
                      <StatFieldInput
                        label={t('frontend.messages.ot_wins')}
                        field="overtime_wins"
                        stats={stats}
                        onChange={handleChange}
                      />
                      <StatFieldInput
                        label={t('frontend.messages.so_wins')}
                        field="shootout_wins"
                        stats={stats}
                        onChange={handleChange}
                      />
                    </>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatFieldInput
                    label={t('frontend.messages.losses')}
                    field="losses"
                    stats={stats}
                    onChange={handleChange}
                  />
                  <StatFieldInput
                    label={t('frontend.messages.ot_losses')}
                    field="overtime_losses"
                    stats={stats}
                    onChange={handleChange}
                  />
                  <StatFieldInput
                    label={t('frontend.messages.so_losses')}
                    field="shootout_losses"
                    stats={stats}
                    onChange={handleChange}
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatFieldInput
                    label={t('frontend.messages.ties')}
                    field="ties"
                    stats={stats}
                    onChange={handleChange}
                  />
                </div>
                {pointSystem === 'custom' && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatFieldInput
                      label={t('frontend.messages.points')}
                      field="points"
                      stats={stats}
                      onChange={handleChange}
                    />
                    <StatFieldInput
                      label={t('frontend.messages.win')}
                      field="win_pct"
                      stats={stats}
                      onChange={handleChange}
                    />
                  </div>
                )}
              </FieldGroup>
            </Fieldset>
          </Card.Content>
        </Card>

        <Card className="p-6">
          <Card.Header>
            <Card.Title>{t('frontend.messages.scoring')}</Card.Title>
          </Card.Header>
          <Card.Content>
            <Fieldset>
              <FieldGroup>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatFieldInput
                    label={t('frontend.messages.goals_for')}
                    field="goals_for"
                    stats={stats}
                    onChange={handleChange}
                  />
                  <StatFieldInput
                    label={t('frontend.messages.goals_against')}
                    field="goals_against"
                    stats={stats}
                    onChange={handleChange}
                  />
                </div>
              </FieldGroup>
            </Fieldset>
          </Card.Content>
        </Card>

        <Card className="p-6">
          <Card.Header>
            <Card.Title>{t('frontend.messages.special_teams')}</Card.Title>
          </Card.Header>
          <Card.Content>
            <Fieldset>
              <FieldGroup>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatFieldInput
                    label={t('frontend.messages.power_play')}
                    field="power_play_pct"
                    stats={stats}
                    onChange={handleChange}
                  />
                  <StatFieldInput
                    label={t('frontend.messages.penalty_kill')}
                    field="penalty_kill_pct"
                    stats={stats}
                    onChange={handleChange}
                  />
                </div>
              </FieldGroup>
            </Fieldset>
          </Card.Content>
        </Card>
      </div>
    </div>
  );
};

export const Route = createFileRoute(
  '/_authenticated/$owner/active/$team_season_id/stats'
)({
  component: AdminStats
});
